import {ServerSymbolPosition, ServerWinLine} from '@fantasmagames/prototype/src/win/ServerWinLine';
import {LAST_SYMBOL, ServerSymbols, W} from '@fantasmagames/prototype/src/symbols/ServerSymbols';

interface WinLineParams {
	MINIMUM_SYMBOLS_TO_WIN: number;
	LEFT_TO_RIGHT: boolean;
	RIGHT_TO_LEFT: boolean;
	FROM_CORNER: boolean;
}

export class ServerWaysWinChecker {
	public static readonly defaultParams: WinLineParams = {
		FROM_CORNER: true,
		LEFT_TO_RIGHT: true,
		RIGHT_TO_LEFT: false,
		MINIMUM_SYMBOLS_TO_WIN: 3,
	};

	protected _currentWinningParams: WinLineParams;

	public findWins(reels: number[][], specialParams?: WinLineParams): ServerWinLine[] {
		if (!specialParams) {
			this._currentWinningParams = ServerWaysWinChecker.defaultParams;
		} else {
			this._currentWinningParams = specialParams;
		}

		const lines: ServerWinLine[] = [];

		for (let symbol = 0; symbol < LAST_SYMBOL; symbol++) {
			if (ServerSymbols.isOrdinarySymbol(symbol)) {
				const symMatrix: number[][] = this.getSymbolsWinMatrixWithWilds(reels, symbol);
				if (symMatrix) {
					const line: ServerWinLine = this.tryToMakeWinLineFormMatrix(symMatrix, symbol);
					if (line) {
						lines.push(line);
					}
				}
			}
		}

		return lines;
	}

	public getSymbolOnReelPositions(symbol: number, reelIndex: number, reels: number[][]): number[] {
		const positions: number[] = []
		for (let i = 0; i < reels[reelIndex].length; i++) {
			if (reels[reelIndex][i] === symbol) {
				positions.push(i);
			}
		}

		return positions;
	}

	protected getSymbolsWinMatrixWithWilds(reels: number[][], symbol: number): number[][] {
		const symbolPositions: ServerSymbolPosition[] = this.getAllPositionsOfSymbol(reels, symbol);
		if (symbolPositions.length < 1) {
			return null;
		}

		const wildsPositions: ServerSymbolPosition[] = [];
		const wilds: number[] = ServerSymbols.getWilds();
		for (const wild of wilds) {
			wildsPositions.push(...this.getAllPositionsOfSymbol(reels, wild));
		}

		const symbolsMatrix: number[][] = [];
		for (let reelIndex = 0; reelIndex < reels.length; reelIndex++) {
			symbolsMatrix[reelIndex] = [];
			for (const row of reels[reelIndex]) {
				symbolsMatrix[reelIndex].push(0);
			}
		}

		for (const wp of wildsPositions) {
			symbolsMatrix[wp.reel][wp.row] = 2;
		}

		for (const sp of symbolPositions) {
			symbolsMatrix[sp.reel][sp.row] = 1;
		}

		return symbolsMatrix;
	}

	protected tryToMakeWinLineFormMatrix(symbolMatrix: number[][], symbol: number): ServerWinLine {
		if (this.hasLineInMatrix(symbolMatrix)) {
			const line: ServerWinLine = new ServerWinLine();
			line.mainWinSymbol = symbol;
			line.multiplier = 1;
			line.win = 1;
			for (let reelIndex = 0; reelIndex < symbolMatrix.length; reelIndex++) {
				let symsOnReel: number = 0;
				for (let rowIndex = 0; rowIndex < symbolMatrix[reelIndex].length; rowIndex++) {
					if (symbolMatrix[reelIndex][rowIndex] === 1) {
						line.addWinPosition(reelIndex, rowIndex, symbol);
						symsOnReel++;
					}

					if (symbolMatrix[reelIndex][rowIndex] === 2) {
						line.addWinPosition(reelIndex, rowIndex, W);
						symsOnReel++;
					}
				}
				if (symsOnReel === 0) {
					return line;
				} else {
					line.multiplier *= symsOnReel;
				}
			}

			return line;
		}

		return null;
	}

	protected hasLineInMatrix(symbolMatrix: number[][]): boolean {
		for (let reelIndex = 0; reelIndex < symbolMatrix.length; reelIndex++) {
			const positionsOnCurrentReel: ServerSymbolPosition[] = [];
			for (let rowIndex = 0; rowIndex < symbolMatrix[reelIndex].length; rowIndex++) {
				if (symbolMatrix[reelIndex][rowIndex] > 0) {
					positionsOnCurrentReel.push({reel: reelIndex, row: rowIndex});
				}
			}
			if (reelIndex >= this._currentWinningParams.MINIMUM_SYMBOLS_TO_WIN) {
				return true;
			}
			if (positionsOnCurrentReel.length <= 0) {
				return false;
			}
		}
	}

	protected getAllPositionsOfSymbol(reels: number[][], symbol: number): ServerSymbolPosition[] {
		const positions: ServerSymbolPosition[] = [];
		for (let reelIndex = 0; reelIndex < reels.length; reelIndex++) {
			for (let rowIndex = 0; rowIndex < reels[reelIndex].length; rowIndex++) {
				if (reels[reelIndex][rowIndex] === symbol) {
					positions.push({reel: reelIndex, row: rowIndex});
				}
			}
		}

		return positions;
	}

}


// WEBPACK FOOTER //
// ./@fantasmagames/prototype/src/win/ServerWaysWinChecker.ts